/**
 * ============================================================================
 * File Module : CarrierManagement
 * File Path   : src/components/Admin/CarrierManagement/CarrierManagement.jsx
 * Purpose     : Admin listing of integrated carriers with toggles to enable /
 *               disable rate search and mark the carrier bookable or not.
 *               Two tabs: Air (below) and Ocean (OceanCarrierList.jsx).
 *               Table + pagination styling mirrors the Booking History list.
 * API (Air)   : GET   allowed_airline_list/:userId
 *                     -> { success, data: { success, message, data: [rows] } }
 *                     Each row: id = allowed-airline ROW id, airline_id = the
 *                     airline's own id.
 *               PATCH allowed_airline/auto_flags
 *                     { id (row id), user_id, Rates_from, auto_search?|auto_book? }
 *                     400 bad field / Rates_from mismatch, 403 not allowed,
 *                     404 row not found, 409 row inactive.
 *               auto_search 0 = rate search enabled (searched normally);
 *                           1 = disabled (airline skipped on a fresh search,
 *                           and hidden from the Preferred Airline filter).
 *               auto_book   0 = bookable (user sees Proceed to Pay);
 *                           1 = non-bookable (user sees Submit Indent — the
 *                           booking is raised without the payment step).
 * Module      : Admin / Carrier Management
 * ============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import { Input, Switch, Table, Tabs, Tooltip } from "antd";
import { httpinstance } from "../../../axios/api";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Plane, Search, Ship } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomeHeader from "../Common/CustomeHeader";
import { MainContainer } from "../../../StyledComponents/Dashboard";
import { ContentContainer, TableTitle, TableTop } from "../../../StyledComponents/CreateKeyAccountManager";
import { RightSection } from "../../../StyledComponents/Queries";
import useExcel from "../../../utils/generateExcelFile";
import CarrierPagination from "./CarrierPagination";
import OceanCarrierList from "./OceanCarrierList";
import "../../BookingHistory/AirCargoList.css";
import "./CarrierManagement.css";

// Auto flags can only be set on user 35's allowed-airline list (backend rule),
// so this page manages that list only.
const ALLOWED_AIRLINE_USER_ID = 35;

// Backend wraps the airline API's response: { success, data: <airline API> },
// so messages may sit one level down. Prefer the innermost one.
const apiMessage = (body) => body?.data?.message || body?.message;

// Display labels for the Rates_from codes. Unknown codes fall back to the raw
// value so a new platform still shows up instead of rendering blank.
// FS = Freightos (WebCargo), direct = carrier's own API, offline = no live rates.
const PLATFORM_LABELS = {
  FS: "Webcargo",
  DIRECT: "Direct",
  OFFLINE: "Offline",
};
const platformLabel = (code) =>
  code ? PLATFORM_LABELS[String(code).toUpperCase()] || code : "-";

// auto_book / auto_search arrive as 0/1 (sometimes "0"/"1", or null).
// Inverted meaning for both flags: 0 = enabled / bookable, 1 = disabled /
// non-bookable. A missing value counts as 0 (enabled).
const isEnabled = (flag) => Number(flag) !== 1;
// Value to send for a switch position: on (enabled) -> 0, off (disabled) -> 1.
const flagValue = (enabled) => (enabled ? 0 : 1);

// The allowed-airline ROW id — what the PATCH body's `id` must be. Depending on
// the list's shape this is `allowed_airline_id` (with `id` = airline's own id)
// or `id` itself, so prefer allowed_airline_id when present.
const rowIdOf = (row) => row?.allowed_airline_id ?? row?.id;

const AirCarrierList = () => {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // "<id>-<field>" of switches with a PATCH in flight, so only that switch spins.
  const [savingKeys, setSavingKeys] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const exceSheetlData = useExcel();

  // Search matches the text shown in each column (name, platform label,
  // Enabled/Disabled, Bookable/Non-Bookable), case-insensitively.
  const filteredCarriers = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return carriers;
    return carriers.filter((c) =>
      [
        c.carr_name,
        platformLabel(c.Rates_from),
        isEnabled(c.auto_search) ? "Enabled" : "Disabled",
        isEnabled(c.auto_book) ? "Bookable" : "Non-Bookable",
      ].some((v) => String(v || "").toLowerCase().includes(term))
    );
  }, [carriers, searchText]);

  /**
   * fetchCarriers
   * -------------
   * Loads the allowed-airline rows for the carrier table.
   * API: GET allowed_airline_list/:userId -> res.data.data.data (array)
   */
  const fetchCarriers = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await httpinstance.get(`allowed_airline_list/${ALLOWED_AIRLINE_USER_ID}`);
      const payload = res?.data?.data;
      const list = Array.isArray(payload) ? payload : payload?.data || [];

      // Drop empty items and duplicate rows (same row id) so keys stay unique.
      const seen = new Set();
      const clean = (Array.isArray(list) ? list : []).filter((item) => {
        const rid = rowIdOf(item);
        if (!item || rid == null || seen.has(rid)) return false;
        seen.add(rid);
        return true;
      });
      setCarriers(clean);
      // Data may shrink on reload; don't leave the user on a page that no longer exists.
      setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(clean.length / pageSize))));
    } catch (err) {
      console.error("Failed to load carriers:", err);
      const msg = apiMessage(err?.response?.data) || "Failed to load carriers";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarriers();
  }, []);

  /**
   * downloadExcel
   * -------------
   * Exports every carrier matching the search (not just the current page) to
   * an .xlsx file.
   */
  const downloadExcel = () => {
    const rows = filteredCarriers.map((c, index) => ({
      "S.No": index + 1,
      "Carrier Name": c.carr_name || "-",
      "Integrated Platform": platformLabel(c.Rates_from),
      "Rate Search": isEnabled(c.auto_search) ? "Enabled" : "Disabled",
      "Booking Status": isEnabled(c.auto_book) ? "Bookable" : "Non-Bookable",
    }));
    exceSheetlData.generateExcelFile(rows, "Carrier Management", "carrier-management.xlsx");
    toast.success(`${rows.length} rows of data downloaded in excel`);
  };

  /**
   * handleToggle
   * ------------
   * Saves one flag (auto_search / auto_book) for a carrier. The switch flips
   * immediately and is reverted if the PATCH fails; on success it takes the
   * flag values the server returns. Both 200 messages ("updated successfully"
   * and "already has these values") count as success.
   * Logs the new value (enabled = true, disabled = false).
   *
   * API: PATCH allowed_airline/auto_flags
   *      { id: row id (not airline_id), user_id, Rates_from, [field]: 0|1 }
   */
  const handleToggle = async (record, field, checked) => {
    console.log(`${record.carr_name} - ${field}:`, checked);
    const rowId = rowIdOf(record);
    const key = `${rowId}-${field}`;
    if (savingKeys[key]) return; // a save for this switch is already in flight
    const previous = record[field];
    const setRow = (updater) =>
      setCarriers((prev) => prev.map((c) => (rowIdOf(c) === rowId ? updater(c) : c)));

    // The backend rejects a body without Rates_from (400), so don't send one.
    if (!record.Rates_from) {
      toast.error(`${record.carr_name || "This carrier"} has no integrated platform; can't update.`);
      return;
    }

    setRow((c) => ({ ...c, [field]: flagValue(checked) }));
    setSavingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      const payload = {
        id: rowId,
        user_id: ALLOWED_AIRLINE_USER_ID,
        Rates_from: record.Rates_from,
        [field]: flagValue(checked),
      };
      console.log("auto_flags payload:", payload);

      const res = await httpinstance.patch("allowed_airline/auto_flags", payload);
      // Take only the flags back from the server's row (data.data.*).
      const row = res?.data?.data?.data;
      if (row && typeof row === "object") {
        setRow((c) => ({
          ...c,
          ...(row.auto_book != null && { auto_book: row.auto_book }),
          ...(row.auto_search != null && { auto_search: row.auto_search }),
        }));
      }
      toast.success(apiMessage(res?.data) || "Carrier updated");
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      setRow((c) => ({ ...c, [field]: previous }));
      toast.error(apiMessage(err?.response?.data) || "Failed to update carrier");
    } finally {
      setSavingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const totalData = filteredCarriers.length;
  // A reload can shrink the matches; never show a page past the last one.
  const page = Math.min(currentPage, Math.max(1, Math.ceil(totalData / pageSize)));
  const pageData = filteredCarriers.slice((page - 1) * pageSize, page * pageSize);

  // Filter options come from whatever platforms are actually in the data.
  const platformFilters = [...new Set(carriers.map((c) => c.Rates_from).filter(Boolean))].map(
    (code) => ({ text: platformLabel(code), value: code })
  );

  const columns = [
    {
      title: "S.No",
      key: "slno",
      align: "center",
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Carrier Name",
      key: "carr_name",
      dataIndex: "carr_name",
      align: "center",
      render: (name) => <div className="font-medium">{name || "-"}</div>,
    },
    {
      title: "Integrated Platform",
      key: "Rates_from",
      dataIndex: "Rates_from",
      align: "center",
      render: (code) => platformLabel(code),
      filters: platformFilters,
      onFilter: (value, record) => record.Rates_from === value,
      // Dropdown is portalled to <body>, so it needs its own class to be themed.
      filterDropdownProps: { rootClassName: "carrierFilterDropdown" },
    },
    {
      // auto_search decides whether the airline is searched at all — explain it.
      title: (
        <Tooltip title="Enabled: carrier's rates show in search results. Disabled: carrier is skipped on a new search and hidden from the Preferred Airline filter.">
          <span>Rate Search <InfoCircleOutlined /></span>
        </Tooltip>
      ),
      key: "auto_search",
      align: "center",
      render: (record) => (
        <Switch
          className="carrierToggle"
          checked={isEnabled(record.auto_search)}
          loading={!!savingKeys[`${rowIdOf(record)}-auto_search`]}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
          onChange={(checked) => handleToggle(record, "auto_search", checked)}
        />
      ),
    },
    {
      // auto_book decides the button on the booking page — explain it here.
      title: (
        <Tooltip title="Bookable: user pays via Proceed to Pay. Non-Bookable: user gets Submit Indent (no payment step).">
          <span>Booking Status <InfoCircleOutlined /></span>
        </Tooltip>
      ),
      key: "auto_book",
      align: "center",
      render: (record) => (
        <Switch
          className="carrierToggle"
          checked={isEnabled(record.auto_book)}
          loading={!!savingKeys[`${rowIdOf(record)}-auto_book`]}
          checkedChildren="Bookable"
          unCheckedChildren="Non-Bookable"
          onChange={(checked) => handleToggle(record, "auto_book", checked)}
        />
      ),
    },
  ];


  return (
        <ContentContainer>
          <TableTop>
            <TableTitle>Carrier List</TableTitle>
            <RightSection className="carrierToolbar">
              <Input
                allowClear
                prefix={<Search size={15} color="#a8a29e" />}
                placeholder="Search by carrier, platform or status"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                className="carrierSearch"
              />
              <button
                disabled={totalData === 0}
                onClick={downloadExcel}
                type="button"
                className="text-white bg-primary hover:bg-secondary font-medium rounded-lg text-sm px-4 py-2 my-2 text-center flex items-center justify-center gap-2 shadow-searchbox disabled:opacity-60"
              >
                <DownloadOutlined />
                <span>Download</span>
              </button>
            </RightSection>
          </TableTop>
      <Table
        className="bookingHistoryTable carrierManagementTable"
        rowKey={rowIdOf}
        columns={columns}
        dataSource={pageData}
        loading={loading}
        locale={{
          emptyText: loadError ? (
            <div className="py-6 text-red-700">
              {loadError}{" "}
              <button type="button" className="underline text-black" onClick={fetchCarriers}>
                Retry
              </button>
            </div>
          ) : searchText.trim() && carriers.length > 0 ? (
            <div className="py-6">No carriers match "{searchText.trim()}"</div>
          ) : undefined,
        }}
        scroll={{ x: 900 }}
        pagination={false}
      />
      <CarrierPagination
        total={totalData}
        currentPage={page}
        pageSize={pageSize}
        noun="carriers"
        onPageChange={setCurrentPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setCurrentPage(1);
        }}
      />
        </ContentContainer>
  );
};

const CarrierManagement = () => (
  <MainContainer>
    <ToastContainer position="top-center" autoClose={3000} limit={1} />
    <CustomeHeader
      title="Carrier Management"
      description="View all integrated carriers and manage rate search and booking."
    />
    <div className="mt-4 tableResponsiveASAP">
      {/* Card tabs styled like the Credit Invoices page. antd renders a tab's
          content on first open, so Ocean loads only when its tab is clicked. */}
      <Tabs
        type="card"
        defaultActiveKey="air"
        items={[
          {
            key: "air",
            label: <span className="carrierTabLabel"><Plane size={15} />Air Cargo</span>,
            children: <AirCarrierList />,
          },
          {
            key: "ocean",
            label: <span className="carrierTabLabel"><Ship size={15} />Ocean Cargo</span>,
            children: <OceanCarrierList />,
          },
        ]}
      />
    </div>
  </MainContainer>
);

export default CarrierManagement;
